import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { Routes } from '../../config/routes';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import { useRevocation } from '../../providers/RevocationContext';
import { Match, MatchStatus } from '../../services/faceAuthentication/facetec/facetecProcessor';
import { addressFromDid, getChainAndIcon, truncate } from '../../utils/utils';
import GenericModal from '../ConfirmationModal';
import { GatekeeperNetworkServiceContext } from '../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';

// If the pass / GK record is in one of these states, it can't be marked for revocation:
const DISABLE_CHECKBOX_FOR_STATES = [MatchStatus.IN_REVIEW, MatchStatus.REVOKED, MatchStatus.FROZEN];

function RevocationItems(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { burnCooldownPeriodLabel } = useContext(GatekeeperNetworkServiceContext);
  const { revocationPasses, setSelectedPass, selectedRevocationPasses } = useRevocation();
  const [showModal, setShowModal] = useState(false);

  function handleCheckboxChange(item: Match) {
    setSelectedPass(item);
  }

  const handleContinue = async () => {
    navigate(Routes.REVOCATION_IN_REVIEW);
  };

  const rowClass = (item: Match): string =>
    `${DISABLE_CHECKBOX_FOR_STATES.includes(item.status) ? 'opacity-50' : ''} text-textParagraph mb-4 flex w-full`;

  return (
    <>
      <GenericModal
        showModal={showModal}
        description={'screens.revocationList.modalConfirmDescription'}
        onCancel={() => setShowModal(false)}
        onConfirm={handleContinue}
      />

      <div className="text-textHeadline mb-2 flex w-full">
        <div className="w-1/6"></div>
        <div className="w-3/6">
          <div className="text-textHeadline font-medium">Address</div>
        </div>
        <div className="w-2/6">
          <div className="text-textHeadline font-medium">Chain/Network</div>
        </div>
      </div>

      {/* Subsequent rows */}
      {revocationPasses.map((item, index) => (
        <div
          key={index}
          className={rowClass(item)}
          role="button"
          onClick={() => !DISABLE_CHECKBOX_FOR_STATES.includes(item.status) && handleCheckboxChange(item)}
        >
          <div className="w-1/6">
            <input
              className="form-checkbox checkBox"
              type="checkbox"
              data-testid="REVOCATION_CHECKBOX"
              disabled={DISABLE_CHECKBOX_FOR_STATES.includes(item.status)}
              checked={selectedRevocationPasses.includes(item)}
              onChange={() => handleCheckboxChange(item)}
            />
          </div>
          <div className="w-3/6">
            <span className="textParagraph">{truncate(addressFromDid(item.did), 16)}</span>
            <br />
            <small>{item.status !== MatchStatus.ACTIVE ? item.status : ''}</small>
          </div>
          <div className="flex w-2/6">
            <div>
              <img className="w-6 mr-2" src={`/icons/chainIcons/${getChainAndIcon(item)?.icon}.svg`} alt="" />
            </div>
            <div>
              <span className="textParagraph">{getChainAndIcon(item)?.chain}</span>
            </div>
          </div>
        </div>
      ))}
      <p dangerouslySetInnerHTML={{ __html: t('screens.revocationList.description', { burnCooldownPeriodLabel }) }}></p>
      <ButtonContainer bottomMargin="mb-2 mt-4 mb-5 max-w-sm">
        <Button
          style={ButtonStyle.DEFAULT}
          disabled={selectedRevocationPasses.length === 0}
          dataTestid="CONFIRM_SHARE_BUTTON"
          onClick={() => setShowModal(true)}
        >
          <span>{t('screens.revocationList.revokeButton')}</span>
        </Button>
      </ButtonContainer>
    </>
  );
}
function RevocationListScreen({ testId }: { testId: string }): JSX.Element {
  const { getExternalLinksConfig } = useContext(FlowConfigContext);
  const { civicSupportLink } = getExternalLinksConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { revocationPasses } = useRevocation();
  const hasPasses = revocationPasses.length > 0;
  const { t } = useTranslation();

  const handleContactSupport = () => {
    navigate(Routes.SUPPORT, { state: { from: location.pathname } });
  };

  return (
    <Container>
      <TitleContainer center>
        <h1 className="mb-2" data-testid={testId}>
          {t(`screens.revocationList.title`)}
        </h1>
      </TitleContainer>
      {hasPasses ? <RevocationItems /> : <p className="text-center">{t('screens.revocationList.noPasses')}</p>}
      <span className="w-full whitespace-pre-wrap textDetail text-center mt-6">
        {t('common.support.supportLinkQuestions')}
        <a target="_blank" title="Help Center" href={civicSupportLink} className="textLinkDetail" rel="noreferrer">
          {t('common.support.supportLinkVisitHelpCenter')}
        </a>
        {t('common.support.supportLinkOr')}
        <button
          type="button"
          className="buttonLink"
          title="Contact support"
          onClick={handleContactSupport}
          data-testid="CONTACT_SUPPORT_BUTTON"
        >
          {t('common.support.supportLinkContactSupport')}
        </button>
      </span>
    </Container>
  );
}

export default (): JSX.Element =>
  RevocationListScreen({
    testId: 'REVOCATION_LIST_PASSES_SCREEN',
  });
